import { formatMoney } from "@/shared/lib/currency";
import { fromIso8601Date, toLocaleTextAsDate } from "@/shared/lib/datetime";
import { toLocaleNumberText } from "@/shared/lib/number";
import { BaseKeyValue } from "@/shared/ui/BaseKeyValue";
import { BaseHeaderWrapper } from "@/shared/ui/base-header-wrapper";
import { ValueWithLabel } from "@/widgets/value-with-label";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { DraftOfferCargo } from "../../model";

export const CargoInfo = ({
  previewValues,
}: {
  previewValues: DraftOfferCargo;
}) => {
  const { t } = useTranslation();

  return (
    <BaseHeaderWrapper title="荷物情報・その他" maxWidth="628px">
      <BaseKeyValue keyLabel="運賃">
        <ValueWithLabel
          label={t("offerCargo.shippingFee")}
          value={formatMoney(previewValues.shippingFee)}
          emphasis
        />
        <ValueWithLabel
          label={t("offerCargo.highwayFee")}
          value={previewValues.highwayFee ? "あり" : "なし"}
          emphasis
        />
        <ValueWithLabel
          label={t("offerCargo.highwaySection")}
          value={previewValues.highwaySection}
        />
        <ValueWithLabel
          label={t("offerCargo.fuelSurcharge")}
          value={formatMoney(previewValues.fuelSurcharge)}
        />
        <ValueWithLabel
          label={t("offerCargo.cargoHandlingFee")}
          value={formatMoney(previewValues.cargoHandlingFee)}
        />
        <ValueWithLabel
          label={t("offerCargo.othersFee")}
          value={formatMoney(previewValues.othersFee)}
        />
        <ValueWithLabel
          label={t("offerCargo.adjustmentFee")}
          value={formatMoney(previewValues.adjustmentFee)}
        />
        <ValueWithLabel
          label={t("offerCargo.paymentDate")}
          value={
            previewValues.paymentDate
              ? toLocaleTextAsDate(fromIso8601Date(previewValues.paymentDate))
              : undefined
          }
        />
        <ValueWithLabel
          label={t("offerCargo.basicFeeMemo")}
          value={previewValues.basicFeeMemo}
        />
      </BaseKeyValue>
      <BaseKeyValue keyLabel="荷物">
        <ValueWithLabel
          label={t("offerCargo.name")}
          value={previewValues.name}
        />
        <ValueWithLabel
          label={t("offerCargo.packingStyle")}
          value={t(`packingStyle.${previewValues.packingStyle}`)}
        />
        <ValueWithLabel
          label={t("offerCargo.count")}
          value={toLocaleNumberText(previewValues.count)}
        />
        <ValueWithLabel
          label={t("offerCargo.weight")}
          value={toLocaleNumberText(previewValues.weight)}
        />
        <Stack direction="row" alignItems={"center"} spacing={0.5}>
          <Typography variant="body2" color="grey.900">
            {t("size")}
          </Typography>
          <Typography variant="body2" color="grey.900">
            :
          </Typography>
          {!previewValues.depth &&
          !previewValues.width &&
          !previewValues.height ? (
            <Typography variant="body2" color="grey.500">
              未記入
            </Typography>
          ) : (
            <Stack direction="row" alignItems={"center"} spacing={0}>
              <Typography
                key="size-depth"
                variant="body2"
                color={previewValues.depth ? "grey.900" : "grey.500"}
              >
                {previewValues.depth ?? "未記入"}
              </Typography>
              <Typography key="separator-1" variant="body2" color={"grey.500"}>
                x
              </Typography>
              <Typography
                key="size-width"
                variant="body2"
                color={previewValues.width ? "grey.900" : "grey.500"}
              >
                {previewValues.width ?? "未記入"}
              </Typography>
              <Typography key="separator-2" variant="body2" color={"grey.500"}>
                x
              </Typography>
              <Typography
                key="size-height"
                variant="body2"
                color={previewValues.height ? "grey.900" : "grey.500"}
              >
                {previewValues.height ?? "未記入"}
              </Typography>
              <Typography key="unit" variant="body2" color={"grey.500"}>
                cm
              </Typography>
            </Stack>
          )}
        </Stack>
      </BaseKeyValue>
      <BaseKeyValue keyLabel="車両">
        <ValueWithLabel
          label={t("offerCargo.vehicleWeight")}
          value={`${toLocaleNumberText(previewValues.vehicleWeight)}t`}
        />
        <ValueWithLabel
          label={t("offerCargo.vehicleShape")}
          value={t(`vehicleShape.${previewValues.vehicleShape}`)}
        />
        <ValueWithLabel
          label={t("offerCargo.vehicleEquipmentMemo")}
          value={previewValues.vehicleEquipmentMemo}
        />
      </BaseKeyValue>
      <BaseKeyValue keyLabel="その他・特記事項">
        <Typography
          variant="body2"
          color={previewValues.memo ? "grey.900" : "grey.500"}
        >
          {previewValues.memo ?? "未記入"}
        </Typography>
      </BaseKeyValue>
    </BaseHeaderWrapper>
  );
};
